import toInteger from 'lodash/toInteger';
import { getFramework } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { base64Kit } from '@/utils/base64-kit';
import {
    calculateGradientCoordinate,
    convertImageToWebp,
    convertWebpToImage,
    createCanvas,
    drawLayerBaseInfoAsync,
    drawLayerTransform,
    loadImageAsync,
} from '@/utils/file-kit';
import { computerImageTransform, deleteExtraInfo } from '@/utils/layer-kit';
import {
    DynamicType,
    IBackgroundLayerProps,
    IExportBehavior,
    IImageProps,
    ILayerBehavior,
    ILayerProps,
    IRenderLayerExtraProps,
    ISize,
    LayerType,
    StretchOptionType,
} from '@/version-types';
import BackgroundLayer from './views/background-layer';

function getBehavior(identity: string) {
    const layerBehavior: ILayerBehavior = {
        render(layer: ILayerProps, extraProps: IRenderLayerExtraProps) {
            return <BackgroundLayer layer={layer} {...extraProps} />;
        },

        onBeforeAddOrUpdate(layer: ILayerProps) {
            const data = layer.data as IBackgroundLayerProps;

            // 兼容线上旧配方
            if (data.backgroundColor === 'transparent') {
                data.backgroundColor = '#00000000';
            }

            data.url = base64Kit.getCode(convertImageToWebp(data.url));
        },

        async onBeforeSaveAsync(layer: ILayerProps) {
            const data = layer.data as IImageProps;
            data.url = convertWebpToImage(data.url);
            //容错处理
            data.imageTransform.heightRatio = data.imageTransform.heightRatio || 1;
            data.imageTransform.widthRatio = data.imageTransform.widthRatio || 1;
            data.imageTransform.offsetX = data.imageTransform.offsetX || 0;
            data.imageTransform.offsetY = data.imageTransform.offsetY || 0;
            deleteExtraInfo(layer);
            if (data.url) {
                data.url = await base64Kit.getRemoteUrlAsync(data.url);
            }
        },
        async getResultCanvasAsync(layer: ILayerProps, resultSize: ISize): Promise<HTMLCanvasElement | undefined> {
            const { width: stageWidth, height: stageHeight } = resultSize;

            const { width, height, opacity, rotate } = layer;
            const data = layer.data as IBackgroundLayerProps;
            const color = data.backgroundColor;
            const backgroundGradient = data.backgroundGradient || null;
            const { stretchOption, imageTransform, url, dynamicInfo } = data;
            let {
                flipH,
                flipV,
                scale,
                rotate: imgRotate,
                heightRatio,
                widthRatio,
                offsetX: offsetXRatio,
                offsetY: offsetYRatio,
            } = imageTransform;
            const { canvas: layerCanvas, ctx: layerCtx } = await drawLayerBaseInfoAsync(
                width,
                height,
                flipH,
                flipV,
                opacity,
            );

            // 这些值在写入时存在null的情况，进行容错处理
            heightRatio = heightRatio || 1;
            widthRatio = widthRatio || 1;
            offsetXRatio = offsetXRatio || 0;
            offsetYRatio = offsetYRatio || 0;

            if (url) {
                if (
                    (dynamicInfo?.enableBGColor && dynamicInfo?.dynamicType === DynamicType.Video) ||
                    dynamicInfo?.dynamicType === DynamicType.Webp
                ) {
                    layerCtx.fillStyle = (layer.data as IBackgroundLayerProps).backgroundColor;
                    layerCtx.fillRect(0, 0, stageWidth, stageHeight);
                }

                // 等待上传完成
                await base64Kit.getRemoteUrlAsync(url);
                // 设置绘制的真正元素
                let baseImage: HTMLCanvasElement | HTMLImageElement;
                try {
                    baseImage = await loadImageAsync(url);
                } catch (e) {
                    const framework = getFramework(identity);

                    baseImage = createCanvas(1, 1).canvas;
                    framework.events.emitBehaviorErrorImage({ layerId: layer.id, imageUrl: url });
                }

                const naturalHeight = baseImage?.height,
                    naturalWidth = baseImage?.width;

                if (!naturalHeight || !naturalWidth) return;
                // if (domCanvas.getAttribute('ready') === 'false') {
                //     baseImage = await loadImageAsync(url);
                // }
                if (stretchOption === StretchOptionType.repeat) {
                    const column = Math.trunc(height! / naturalHeight) + 1;
                    const row = Math.trunc(width! / naturalWidth) + 1;
                    const x = width! / 2 - naturalWidth / 2;
                    const y = height! / 2 - naturalHeight / 2;
                    for (let i = -column; i < column; i++) {
                        for (let j = -row; j < row; j++) {
                            layerCtx.drawImage(
                                baseImage,
                                x + naturalWidth * j,
                                y + naturalHeight * i,
                                naturalWidth,
                                naturalHeight,
                            );
                        }
                    }
                    layerCtx.fill();
                    // WARN: 拉伸模式为 fill 的逻辑用途未知，暂时注释
                    // } else if (stretchOption === StretchOptionType.fill) {
                    //     const res = getDrawContainParams(baseImage.width, baseImage.height, width, height);
                    //     layerCtx.drawImage(baseImage, res.dx, res.dy, res.dw, res.dh);
                } else {
                    const offsetX = toInteger(offsetXRatio * widthRatio * stageWidth * scale);
                    const offsetY = toInteger(offsetYRatio * heightRatio * stageHeight * scale);
                    const originImgWidth = toInteger(stageWidth * widthRatio);
                    const originImgHeight = toInteger(stageHeight * heightRatio);
                    const scaleWidth = toInteger(originImgWidth * scale);
                    const scaleHeight = toInteger(originImgHeight * scale);
                    const resultWidth = toInteger(stageWidth);
                    const resultHeight = toInteger(stageHeight);
                    const img = baseImage;
                    // 元素图片
                    const { canvas: baseCanvas, ctx: baseCtx } = createCanvas(scaleWidth, scaleHeight);
                    // 图片缩放
                    baseCtx.drawImage(img, 0, 0, img.width, img.height, 0, 0, scaleWidth, scaleHeight);
                    // 图片旋转、偏移
                    const { canvas: transformCanvas, ctx: transformCtx } = createCanvas(scaleWidth, scaleHeight);
                    transformCtx.translate(offsetX + scaleWidth / 2, offsetY + scaleHeight / 2);
                    transformCtx.rotate((Math.PI / 180) * imgRotate);
                    transformCtx.drawImage(
                        baseCanvas,
                        0,
                        0,
                        scaleWidth,
                        scaleHeight,
                        -scaleWidth / 2,
                        -scaleHeight / 2,
                        scaleWidth,
                        scaleHeight,
                    );
                    // 转换，图片 -> 图层，结果图
                    const { canvas: resultCanvas, ctx: resultCtx } = createCanvas(resultWidth, resultHeight);
                    resultCtx.drawImage(
                        transformCanvas,
                        (scaleWidth - resultWidth) / 2,
                        (scaleHeight - resultHeight) / 2,
                        resultWidth,
                        resultHeight,
                        0,
                        0,
                        resultWidth,
                        resultHeight,
                    );
                    // 绘制图层翻转、透明度
                    const { canvas: layerCanvas, ctx: layerCtx } = await drawLayerBaseInfoAsync(
                        resultWidth,
                        resultHeight,
                        flipH,
                        flipV,
                        opacity,
                    );
                    if (
                        dynamicInfo?.enableBGColor &&
                        (dynamicInfo?.dynamicType === DynamicType.Video ||
                            dynamicInfo?.dynamicType === DynamicType.Webp)
                    ) {
                        layerCtx.fillStyle = (layer.data as IBackgroundLayerProps).backgroundColor;
                        layerCtx.fillRect(0, 0, stageWidth, stageHeight);
                    }

                    layerCtx.drawImage(resultCanvas, 0, 0);
                    return layerCanvas;
                }
            } else if (backgroundGradient) {
                const { colors, angle } = backgroundGradient;
                const { x0, y0, x1, y1 } = calculateGradientCoordinate(stageWidth, stageHeight, angle);
                const gradient = layerCtx.createLinearGradient(x0, y0, x1, y1);
                colors.forEach((item) => {
                    const { color, value } = item;
                    gradient.addColorStop(value, color);
                });
                layerCtx.fillStyle = gradient;
                layerCtx.fillRect(0, 0, stageWidth, stageHeight);
            } else if (color) {
                layerCtx.fillStyle = color;
                layerCtx.fillRect(0, 0, stageWidth, stageHeight);
                layer.width = stageWidth;
                layer.height = stageHeight;
            }
            return drawLayerTransform(layer, rotate + imgRotate, scale, layerCanvas, stageWidth, stageHeight);
        },

        async savePPTLayerAsync(slide, layer, stageWidth, stageHeight, watermark) {
            const canvas = await layerBehavior.getResultCanvasAsync?.(layer, {
                width: stageWidth,
                height: stageHeight,
            });
            if (watermark) {
                const ctx = canvas?.getContext('2d');
                ctx?.drawImage(
                    watermark,
                    0,
                    0,
                    watermark.width,
                    watermark.height,
                    layer.left + layer.width - 50 - watermark.width,
                    layer.top + layer.height - 50 - watermark.height,
                    watermark.width,
                    watermark.height,
                );
            }
            slide.background = { data: canvas?.toDataURL() };
        },
        checkDisableCollision(): boolean {
            return true;
        },
        checkDisableInGroup(): boolean {
            return true;
        },
        checkDisableActive(): boolean {
            return true;
        },
        checkDisableFlip() {
            return true;
        },
        isBackgroundLayer() {
            return true;
        },
        adjustHeight(layer: ILayerProps, newHeight: number) {
            const data = layer.data as IBackgroundLayerProps;
            const newWidth = layer.width;
            layer.height = newHeight;
            if (!data.url) return;
            computerImageTransform(newWidth, newHeight, data);
        },
        adjustWidth(layer: ILayerProps, newWidth: number) {
            const data = layer.data as IBackgroundLayerProps;
            const newHeight = layer.height;
            layer.width = newWidth;
            if (!data.url) return;
            computerImageTransform(newWidth, newHeight, data);
        },
        adjustResize(layer: ILayerProps, newWidth: number, newHeight: number) {
            const data = layer.data as IBackgroundLayerProps;
            layer.height = newHeight;
            layer.width = newWidth;
            if (!data.url) return;
            computerImageTransform(newWidth, newHeight, data);
        },
        getMenuOverlapName(layer: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.background);
        },
    };

    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.BACKGROUND_LAYER,
    getBehavior,
};

export default exportBehavior;
